<template>
  <q-dialog
    v-model="shown"
    maximized
    class="scroll"
    data-test-id="membership-type-selector"
  >
    <div
      class="fullscreen row items-center justify-center scroll"
      style="box-shadow: none;"
      @click="shown = false"
    >
      <q-card
        v-for="membershipTypeData in membershipTypesForUser"
        :key="membershipTypeData.membershipType.id"
        class="col q-ma-md"
        style="max-width:250px; width: 250px; min-width: 200px;"
        :clickable="membershipTypeData.enabled"
        :style="{ opacity: membershipTypeData.enabled ? '1' : '0.75' } "
        :class="{
          selected: selected(membershipTypeData.membershipType),
          'cursor-pointer': membershipTypeData.enabled
        }"
        data-test-id="membership-type-selector-card"
        @click.stop="membershipTypeData.enabled && select(membershipTypeData.membershipType)"
      >
        <q-card-section
          class="row items-center q-mx-xs"
        >
          <div class="text-h5">
            {{ membershipTypeData.membershipType.name }}
          </div>
          <q-space />
          <q-icon
            v-if="!$register && invalidMembershipTypeWarnings[membershipTypeData.membershipType.id]"
            name="warning"
            color="warning"
            size="md"
          >
            <q-tooltip class="bg-warning">
              {{ invalidMembershipTypeWarnings[membershipTypeData.membershipType.id] }}
            </q-tooltip>
          </q-icon>
        </q-card-section>

        <q-separator />

        <q-card-section>
          <!-- eslint-disable vue/no-v-html -->
          <!-- v-html is limited to rich text -->
          <div
            v-if="!!membershipTypeData.membershipType.description"
            class="q-pt-sm q-mb-md"
            v-html="membershipTypeData.membershipType.description"
          />
          <!-- eslint-enable vue/no-v-html -->
          <div v-if="membershipTypeData.membershipType.descriptionType === 'prefix'">
            <div
              class="text-bold q-pt-sm"
            >
              Track and Rate Movies
            </div>
            <div
              class="q-mb-md"
            >
              Keep track of all the movies you've seen! Get customized recommendations.
            </div>
            <div
              v-if="membershipTypeData.membershipType.hasUnconditionalPoints"
              class="text-bold q-pt-sm"
            >
              Earn {{ currentCircuit.rewardsCurrencyName }}
            </div>
            <div
              v-if="membershipTypeData.membershipType.hasUnconditionalPoints && membershipTypeData.membershipType.unconditionalVisitPoints"
              class="q-mb-md"
            >
              Earn {{ membershipTypeData.membershipType.unconditionalVisitPoints }} {{ currentCircuit.rewardsCurrencyName }} for every visit
            </div>
            <div
              v-if="membershipTypeData.membershipType.hasUnconditionalPoints && membershipTypeData.membershipType.unconditionalConcessionsPoints"
              class="q-mb-md"
            >
              Earn {{ membershipTypeData.membershipType.unconditionalConcessionsPoints }} {{ currentCircuit.rewardsCurrencyName }} for every {{ currentCircuit.localeCurrencyName.toLowerCase() }} spent on concessions
            </div>
            <div
              v-if="membershipTypeData.membershipType.hasUnconditionalPoints && membershipTypeData.membershipType.unconditionalTicketPricePoints"
              class="q-mb-md"
            >
              Earn {{ membershipTypeData.membershipType.unconditionalTicketPricePoints }} {{ currentCircuit.rewardsCurrencyName }} for every {{ currentCircuit.localeCurrencyName.toLowerCase() }} spent on tickets
            </div>
            <div
              v-if="membershipTypeData.membershipType.hasUnconditionalPoints && membershipTypeData.membershipType.unconditionalTicketValuePoints"
              class="q-mb-md"
            >
              Earn {{ membershipTypeData.membershipType.unconditionalTicketValuePoints }} {{ currentCircuit.rewardsCurrencyName }} for every {{ currentCircuit.localeCurrencyName.toLowerCase() }} of ticket value
            </div>

            <div
              v-if="membershipTypeData.membershipType.hasMonthlyTicketVouchers || membershipTypeData.membershipType.hasAnnualTicketVouchers || membershipTypeData.membershipType.hasUnlimitedTicketVouchers"
              class="text-bold q-pt-sm"
            >
              {{ $tc('ticket.ticket', 2) }}
            </div>
            <div
              v-if="membershipTypeData.membershipType.hasUnlimitedTicketVouchers"
              class="q-mb-md"
            >
              <!-- This currently advertises unconditional tickets only. -->
              Watch UNLIMITED movies
              (tickets are non-transferable, for member only, not their guests)
            </div>
            <div
              v-if="membershipTypeData.membershipType.hasMonthlyTicketVouchers || membershipTypeData.membershipType.hasAnnualTicketVouchers"
              class="q-mb-md"
            >
              Get {{ membershipTypeData.membershipType.hasMonthlyTicketVouchers ? membershipTypeData.membershipType.monthlyTicketVouchersCount : membershipTypeData.membershipType.annualTicketVouchersCount }}
              FREE ticket{{ (membershipTypeData.membershipType.hasMonthlyTicketVouchers && membershipTypeData.membershipType.monthlyTicketVouchersCount > 1) || (membershipTypeData.membershipType.hasAnnualTicketVouchers && membershipTypeData.membershipType.annualTicketVouchersCount > 1) ? 's' : '' }}
              every {{ membershipTypeData.membershipType.hasMonthlyTicketVouchers ? 'month' : 'year' }}!
            </div>

            <div v-if="membershipTypeData.membershipType.unconditionalMembershipDiscounts.length">
              <div
                class="text-bold q-pt-sm"
              >
                Discounts
              </div>
              <div
                v-for="membershipDiscount in membershipTypeData.membershipType.unconditionalMembershipDiscounts"
                :key="membershipDiscount.id"
                class="q-mb-md"
              >
                <span>
                  {{ membershipDiscount.discount.description || membershipDiscount.name + (membershipDiscount.limitRedemptions ? ' (Up to ' + membershipDiscount.redemptionsLimit + ' redemptions ' + membershipDiscount.renews + ')' : '') }}
                </span>
              </div>
            </div>

            <div v-if="membershipTypeData.membershipType.hasTiers">
              <div
                class="text-bold q-pt-sm"
              >
                Tiers
              </div>
              <div
                class="q-mb-md"
              >
                Earn new and better rewards while using your membership!
              </div>
            </div>
          </div>

          <div v-if="membershipTypeData.membershipType.hasTiers">
            <q-tabs
              :key="membershipTiersTabsKey"
              v-model="membershipTiersForTypes[membershipTypeData.membershipType.id]"
              dense
              class="text-grey"
              active-color="primary"
              indicator-color="primary"
              align="justify"
              narrow-indicator
              @update:model-value="input => selectTier(membershipTypeData.membershipType.id, input)"
              @click.stop="() => {}"
            >
              <q-tab
                v-for="membershipTier in membershipTypeData.membershipType.tiersReversed"
                :key="membershipTier.name"
                :name="membershipTier.name"
                :label="$_.startCase(membershipTier.name)"
              />
            </q-tabs>

            <q-separator />

            <q-tab-panels
              :key="membershipTiersTabPanelsKey"
              v-model="membershipTiersForTypes[membershipTypeData.membershipType.id]"
              animated
            >
              <q-tab-panel
                v-for="membershipTier in membershipTypeData.membershipType.membershipTiers.data"
                :key="membershipTier.name"
                :name="membershipTier.name"
              >
                <!-- eslint-disable vue/no-v-html -->
                <!-- v-html is limited to rich text -->
                <div
                  class="q-pt-sm q-mb-md"
                  v-html="membershipTier.description"
                />
                <!-- eslint-enable vue/no-v-html -->
                <div v-if="membershipTier.descriptionType === 'prefix'">
                  <div
                    v-if="membershipTier.isConditional"
                    class="q-mb-md"
                  >
                    {{ membershipTier.conditionsDescription }}
                  </div>
                  <div
                    v-if="membershipTier.hasUnconditionalPoints"
                    class="text-bold q-pt-sm"
                  >
                    Earn {{ currentCircuit.rewardsCurrencyName }}
                  </div>
                  <div
                    v-if="membershipTier.hasUnconditionalPoints && membershipTier.unconditionalVisitPoints"
                    class="q-mb-md"
                  >
                    Earn {{ membershipTier.unconditionalVisitPoints }} {{ currentCircuit.rewardsCurrencyName }} for every visit
                  </div>
                  <div
                    v-if="membershipTier.hasUnconditionalPoints && membershipTier.unconditionalConcessionsPoints"
                    class="q-mb-md"
                  >
                    Earn {{ membershipTier.unconditionalConcessionsPoints }} {{ currentCircuit.rewardsCurrencyName }} for every {{ currentCircuit.localeCurrencyName.toLowerCase() }} spent on concessions
                  </div>
                  <div
                    v-if="membershipTier.hasUnconditionalPoints && membershipTier.unconditionalTicketPricePoints"
                    class="q-mb-md"
                  >
                    Earn {{ membershipTier.unconditionalTicketPricePoints }} {{ currentCircuit.rewardsCurrencyName }} for every {{ currentCircuit.localeCurrencyName.toLowerCase() }} spent on tickets
                  </div>
                  <div
                    v-if="membershipTier.hasUnconditionalPoints && membershipTier.unconditionalTicketValuePoints"
                    class="q-mb-md"
                  >
                    Earn {{ membershipTier.unconditionalTicketValuePoints }} {{ currentCircuit.rewardsCurrencyName }} for every {{ currentCircuit.localeCurrencyName.toLowerCase() }} of ticket value
                  </div>

                  <div
                    v-if="membershipTier.hasMonthlyTicketVouchers || membershipTier.hasAnnualTicketVouchers || membershipTier.hasUnlimitedTicketVouchers"
                    class="text-bold q-pt-sm"
                  >
                    {{ $tc('ticket.ticket', 2) }}
                  </div>
                  <div
                    v-if="membershipTier.hasUnlimitedTicketVouchers"
                    class="q-mb-md"
                  >
                    <!-- This currently advertises unconditional tickets only. -->
                    Watch UNLIMITED movies
                    (tickets are non-transferable, for member only, not their guests)
                  </div>
                  <div
                    v-if="membershipTier.hasMonthlyTicketVouchers || membershipTier.hasAnnualTicketVouchers"
                    class="q-mb-md"
                  >
                    Get {{ membershipTier.hasMonthlyTicketVouchers ? membershipTier.monthlyTicketVouchersCount : membershipTier.annualTicketVouchersCount }}
                    FREE ticket{{ (membershipTier.hasMonthlyTicketVouchers && membershipTier.monthlyTicketVouchersCount > 1) || (membershipTier.hasAnnualTicketVouchers && membershipTier.annualTicketVouchersCount > 1) ? 's' : '' }}
                    every {{ membershipTier.hasMonthlyTicketVouchers ? 'month' : 'year' }}!
                  </div>

                  <div v-if="membershipTier.unconditionalMembershipDiscounts.length">
                    <div
                      class="text-bold q-pt-sm"
                    >
                      Discounts
                    </div>
                    <div
                      v-for="membershipDiscount in membershipTier.unconditionalMembershipDiscounts"
                      :key="membershipDiscount.id"
                      class="q-mb-md"
                    >
                      <span>
                        {{ membershipDiscount.discount.description || membershipDiscount.name + (membershipDiscount.limitRedemptions ? ' (Up to ' + membershipDiscount.redemptionsLimit + ' redemptions ' + membershipDiscount.renews + ')' : '') }}
                      </span>
                    </div>
                  </div>
                </div>
              </q-tab-panel>
            </q-tab-panels>
          </div>

          <div
            v-if="anyMembershipTermOverrides"
            class="text-bold q-pt-sm"
          >
            Renewal Periods
          </div>
          <div
            v-if="anyMembershipTermOverrides"
            class="q-mb-md"
          >
            Available for {{ membershipTypeAvailableTerms(membershipTypeData.membershipType) }} renewals.
          </div>
        </q-card-section>

        <q-card-section>
          <div
            class="text-subtitle2 text-center"
            :class="{ 'q-ml-lg': anyMembershipTermOverrides && !membershipTypeData.membershipType.free && membershipTypeData.enabled }"
          >
            <template v-if="membershipTypeData.enabled">
              <span v-if="membershipTypeData.membershipType.free">{{ $_.toUpper($t('pricing.free')) }}</span>
              <span v-else>
                <i18n-n
                  :value="membershipTypeRateDisplayTotal(membershipTypeData.membershipType)"
                  format="currency"
                /> / {{ membershipRateDisplayPeriod() }}
                <q-btn
                  v-if="anyMembershipTermOverrides"
                  class="q-mb-xs"
                  :icon="shownMembershipTypePrices[membershipTypeData.membershipType.id] ? 'expand_less' : 'expand_more'"
                  flat
                  size="md"
                  @click.stop="shownMembershipTypePrices[membershipTypeData.membershipType.id] = !shownMembershipTypePrices[membershipTypeData.membershipType.id]"
                />
              </span>
            </template>
            <span v-else-if="$register">{{ membershipTypeData.registerMessage }}</span>
          </div>
          <div
            v-if="shownMembershipTypePrices[membershipTypeData.membershipType.id]"
            class="column text-subtitle2 text-grey text-center"
            style="font-size: 0.75rem; line-height: 0.85rem;"
          >
            <span v-if="membershipTypeData.membershipType.allowMonthlyMembership">
              <i18n-n
                :value="membershipTypeRateDisplayTotal(membershipTypeData.membershipType, 1)"
                format="currency"
              /> / {{ membershipRateDisplayPeriod() }} (Monthly)
            </span>
            <span v-if="membershipTypeData.membershipType.allowQuarterlyMembership">
              <i18n-n
                :value="membershipTypeRateDisplayTotal(membershipTypeData.membershipType, 3)"
                format="currency"
              /> / {{ membershipRateDisplayPeriod() }} (Quarterly)
            </span>
            <span v-if="membershipTypeData.membershipType.allowBiannualMembership">
              <i18n-n
                :value="membershipTypeRateDisplayTotal(membershipTypeData.membershipType, 6)"
                format="currency"
              /> / {{ membershipRateDisplayPeriod() }} (Biannual)
            </span>
            <span v-if="membershipTypeData.membershipType.allowAnnualMembership">
              <i18n-n
                :value="membershipTypeRateDisplayTotal(membershipTypeData.membershipType, 12)"
                format="currency"
              /> / {{ membershipRateDisplayPeriod() }} (Annual)
            </span>
          </div>
        </q-card-section>

        <q-card-actions vertical>
          <q-btn
            :color="membershipTypeData.enabled ? 'primary' : 'grey'"
            :disabled="!membershipTypeData.enabled || selected(membershipTypeData.membershipType)"
            data-test-id="membership-type-selector-select-button"
            @click.stop="select(membershipTypeData.membershipType)"
          >
            Select
          </q-btn>
        </q-card-actions>
      </q-card>
    </div>
  </q-dialog>
</template>

<script>
import MembershipType from 'src/models/membership-type/MembershipType'

export default {
  name: 'MembershipTypeSelector',
  props: {
    previewMembershipType: {
      type: MembershipType,
      default: null,
      required: false
    }
  },
  configuration: ['currentSite', 'currentCircuit', 'membershipTypes'],
  emits: ['select-membership-type'],
  data () {
    return {
      shown: false,
      user: null,
      initialMember: true,
      setForAll: false,
      shownMembershipTypePrices: {},
      membershipTiersTabsKey: 'Tabs',
      membershipTiersTabPanelsKey: 'TabPanels'
    }
  },
  computed: {
    anyMembershipTermOverrides () {
      return _.filter(this.membershipTypes.data, 'overrideCircuitTermOptions').length
    },
    membershipTypesForUser () {
      if (this.previewMembershipType) {
        return [{
          membershipType: this.previewMembershipType,
          enabled: true
        }]
      }

      return _(this.membershipTypes.data).filter(membershipType => {
        // Membership Modifications on Admin are unrestricted - exit filter early
        if (this.$admin) return true

        const isOnline = !this.$register
        const isAvailableOnline = membershipType.availableOnline || membershipType.previewOnline
        if (membershipType.hide || (isOnline && !isAvailableOnline)) return false

        if (!membershipType.excludeSites || membershipType.siteIds?.length > 0) {
          if (membershipType.excludeSites === _.includes(membershipType.siteIds, this.currentSite.id)) {
            return false
          }
        }

        return true
      }
      ).map(membershipType => {
        return {
          membershipType,
          enabled: ((this.$register || this.$admin) || membershipType.availableOnline) && (
            (membershipType.free && !membershipType.membershipUserTypes.data.length) || !!membershipType.membershipUserTypeForUser(this.user)
          ) && (!membershipType.ageVerificationRequired || this.user?.dateOfBirthVerified),
          registerMessage: this.registerMessageForMembershipType(membershipType)
        }
      }).compact().value()
    },
    membershipTiersForTypes () {
      const membershipTiersForTypes = {}

      _.each(this.membershipTypesForUser, membershipTypeData => {
        membershipTiersForTypes[membershipTypeData.membershipType.id] = membershipTypeData.membershipType.hasTiers ? membershipTypeData.membershipType.tiersReversed[0].name : ''
      })

      return membershipTiersForTypes
    },
    invalidMembershipTypeWarnings () {
      const invalidMembershipTypes = {}

      const term = this.user?.membership?.term || 1

      _.each(this.membershipTypes.data, membershipType => {
        if (!membershipType.availableOnline && membershipType.previewOnline) {
          invalidMembershipTypes[membershipType.id] = 'This membership type is not available online, please visit the box office'
        } else if (
          (term === 1 && !membershipType.allowMonthlyMembership) ||
          (term === 3 && !membershipType.allowQuarterlyMembership) ||
          (term === 6 && !membershipType.allowBiannualMembership) ||
          (term === 12 && !membershipType.allowAnnualMembership)
        ) {
          invalidMembershipTypes[membershipType.id] = 'This membership type is incompatible with your currently selected renewal period'
        }
      })

      return invalidMembershipTypes
    }
  },
  methods: {
    registerMessageForMembershipType (membershipType) {
      if (membershipType.membershipUserTypeForUser(this.user) && (membershipType.ageVerificationRequired || !this.user?.dateOfBirthVerified)) {
        return 'Membership Requires Age Verification'
      } else if (membershipType.availableMembershipUserTypesForUser(this.user).length) {
        return `Membership Only Available For User Classes: ${
          _.map(membershipType.availableMembershipUserTypesForUser(this.user), membershipUserType => membershipUserType.userClass.name).join(', ')
        }`
      } else {
        return 'Not Available For User'
      }
    },
    show (user, initialMember, setForAll) {
      this.user = user
      this.initialMember = initialMember
      this.setForAll = setForAll
      this.shownMembershipTypePrices = {}
      this.shown = true
    },
    membershipTypeAvailableTerms (membershipType) {
      const availableTerms = []

      if (membershipType.allowMonthlyMembership) {
        availableTerms.push('Monthly')
      }
      if (membershipType.allowQuarterlyMembership) {
        availableTerms.push('Quarterly')
      }
      if (membershipType.allowBiannualMembership) {
        availableTerms.push('Biannual')
      }
      if (membershipType.allowAnnualMembership) {
        availableTerms.push('Annual')
      }

      if (availableTerms.length === 1) {
        return availableTerms[0]
      }

      return `${availableTerms.slice(0, -1).join(', ')} and ${availableTerms[availableTerms.length - 1]}`
    },
    membershipTypeRateDisplayTotal (membershipType, term = this.user?.membership?.term || 1) {
      let rate = 0
      const displayPeriod = this.currentCircuit.membershipRateDisplayPeriod

      const membershipUserType = membershipType.membershipUserTypeForUser(this.user)
      if (membershipUserType) {
        if (this.initialMember) {
          rate = membershipUserType.monthlyRate * displayPeriod
        } else {
          rate = membershipUserType.additionalMonthlyRate * displayPeriod
        }
        if (membershipType.getDiscountPercent(term)) {
          rate = rate * (100 - (membershipType.getDiscountPercent(term) || 0)) / 100
        }
      }

      return rate
    },
    selected (membershipType) {
      if (this.previewMembershipType) return true

      return (this.user && this.user.membershipType && this.user.membershipType.id) === membershipType.id
    },
    select (membershipType) {
      if (this.previewMembershipType) {
        this.shown = false
        return
      }

      if (this.setForAll) {
        _.each(this.user.membership.members.data, member => { member.membershipType = membershipType })
      } else {
        this.user.membershipType = membershipType
      }

      // Set timeout so that the user will see the selection before the animation of hiding the display
      setTimeout(() => { this.shown = false }, 150)
      this.$emit('select-membership-type', membershipType)
    },
    selectTier (membershipTypeId, tier) {
      this.membershipTiersTabsKey = `Tabs${tier}${membershipTypeId}`
      this.membershipTiersTabPanelsKey = `TabPanels${tier}${membershipTypeId}`
      this.membershipTiersForTypes[membershipTypeId] = tier
    },
    membershipRateDisplayPeriod () {
      switch (this.currentCircuit.membershipRateDisplayPeriod) {
        case 1:
          return 'month'
        case 12:
          return 'year'
        default:
          // Something has gone wrong
          return 'month'
      }
    }
  }
}

</script>

<style lang="scss" scoped>
  .selected {
    border: solid 4px;
    border-radius: 10px;
    border-color: var(--q-primary);
  }
</style>
